import React from "react";
import PropTypes from "prop-types";
import { statusTone } from "./ownershipFormat";

// Calm by default — matching the Economic Control summary: a plain white card with a subtle,
// colour-coded attention eyebrow that appears ONLY when something needs attention. No heavy colour
// wash, no thick accent border, no oversized headline. Healthy states show the framing question.
// Graduated like Financial Control: any off-track state reads as a calm amber "Needs attention";
// red "Action needed" is reserved for the genuinely severe case (the page's `severe` flag).
const NEEDS = { label: "Needs attention", dot: "bg-warning", text: "text-warning" };
const SEVERE = { label: "Action needed", dot: "bg-danger", text: "text-danger" };

// OwnershipVerdict is the single answer to "who owns this spend — and is anything unowned?".
const OwnershipVerdict = ({ overall = {}, tone, reassurance, severe = false }) => {
  const t = tone || statusTone(overall.status || "green");
  const hasAttention = t === "attention" || t === "critical";
  const attn = hasAttention ? (severe ? SEVERE : NEEDS) : null; // null when ok / neutral
  const headline = overall.headline || "All spend is clearly owned.";
  const detail = [reassurance, overall.subline].filter(Boolean).join(" · ");
  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4">
      {attn ? (
        <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${attn.text}`}>
          <span className={`inline-block rounded-full ${attn.dot}`} style={{ width: 8, height: 8 }} />
          {attn.label}
        </div>
      ) : (
        <div className="text-xs uppercase tracking-wide text-text-3">Who owns this spend — and is anything unowned?</div>
      )}
      <div className="mt-1.5 text-base font-semibold text-text">{headline}</div>
      {detail && <div className="mt-1 text-sm text-text-2">{detail}</div>}
    </div>
  );
};

OwnershipVerdict.propTypes = {
  overall: PropTypes.shape({
    status: PropTypes.string,
    headline: PropTypes.string,
    subline: PropTypes.string,
  }),
  tone: PropTypes.oneOf(["ok", "attention", "critical", "neutral"]),
  reassurance: PropTypes.string,
  severe: PropTypes.bool,
};

export default OwnershipVerdict;
